import autobind from 'core-decorators/lib/autobind';
import { EventEmitter } from 'eventemitter3';
import _ from 'lodash';
import { getPaymentStatus } from './dgs';
import { STATUSES_OK, STATUSES_PENDING, STATUS_CANCEL } from 'shared/constants/payment-statuses';

const CASHIER_HOST = 'https://cashier.wix.com';

export const NO_PAYMENT_METHODS = 'no_payment_methods';
export const UNKNOWN_ERROR = 'unknown_error';

export class CashierPaymentsWrapper {
  constructor(paymentsSDK) {
    this.instance = paymentsSDK;
    this.events = new EventEmitter();
  }

  /**
   * Initialize Cashier SDK instance for payment
   * @param {object} config
   * @param {string} config.orderId
   * @param {string} config.appDefId
   * @param {string} config.instanceId
   * @param {string} config.instance
   * @param {string} config.locale
   * @return {Promise<{paymentTypes: any[]}>}
   */
  init({ orderId, appDefId, instanceId, instance, locale }) {
    this.config = { orderId, appDefId, instanceId, instance, locale };
    this.instance.init({
      type: 'RAW',
      host: CASHIER_HOST,
      appId: appDefId,
      snapshotId: orderId,
      appInstanceId: instanceId,
      walletPaymentInNewTab: true,
      noPrefetchOnInit: true,
      query: {
        appDefId,
        instanceId,
        orderId,
        locale,
        submitType: 'external',
      },
      onError: this.handleEvent('onError'),
      onMessage: this.handleEvent('onMessage'),
      onPaymentStart: this.handleEvent('onStart'),
      onPaymentComplete: this.handlePaymentComplete,
      onPaymentMethodChanged: this.handleEvent('onMethodChanged'),
    });
    return this.instance
      .fetchSettings({
        appId: appDefId,
        appInstanceId: instanceId,
        snapshotId: orderId,
      }, CASHIER_HOST);
  }

  /**
   * @private
   * @param {string} eventName
   * @returns {function(*, *)}
   */
  handleEvent(eventName) {
    return (method, message) => {
      this.events.emit(eventName, { ...message, method });
    };
  }

  @autobind
  handlePaymentComplete(method, message) {
    return this.checkTransaction({ method, ...message })
      .then(this.handleStatus, this.handleStatus);
  }

  @autobind
  checkTransaction(event) {
    if (event.chargeId && _.includes(STATUSES_OK, event.status)) {
      return getPaymentStatus({
        transactionId: event.chargeId,
        orderId: this.config.orderId,
        instance: this.config.instance,
      }).then(() => event);
    }
    return Promise.resolve(event);
  }

  @autobind
  handleStatus(event) {
    if (_.includes(STATUSES_OK, event.status)) {
      this.events.emit('onSuccess', event);
    } else if (event.status === STATUS_CANCEL) {
      this.events.emit('onCancel', event);
    } else if (_.includes(STATUSES_PENDING, event.status)) {
      this.events.emit('onPending', event);
    } else {
      this.events.emit('onFail', event);
    }
  }

  /**
   * Subscribes to SDK events
   * @param {object} handlersMap
   * @return {function()}
   */
  subscribe(handlersMap) {
    const unsubscribers = _.map(handlersMap, (handler, event) => {
      this.events.on(event, handler);
      return () => {
        this.events.off(event, handler);
      };
    });
    return _.flow(unsubscribers);
  }
}

/**
 * Loads and wraps Cashier SDK
 * @return {Promise<CashierPaymentsWrapper>}
 */
export const getPaymentsSDK = _.memoize(() =>
  import(/* webpackChunkName: 'cashier-payments-sdk'*/'@wix/cashier-payments-sdk')
    .then(module => module.default || module)
    .then(paymentsSDK => new CashierPaymentsWrapper(paymentsSDK))
);

export const configureSDK = config =>
  getPaymentsSDK().then(sdk => sdk.init(config));



// WEBPACK FOOTER //
// ./src/api/payments/cashier.js